import { toast } from "sonner";
import { useState, useContext, useEffect, useMemo } from "react";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Trash2 } from "lucide-react";

type DomusSource = {
  id: number;
  name: string;
};

type DomusTag = {
  code: number;
  name: string;
};

type SourceEntry = {
  key: string;
  label: string;
  required: boolean;
  group: "portal" | "campaign";
};

type DomusGlobalVariablesConfigurationProps = {
  crmConnection: any;
};

const DomusGlobalVariablesConfiguration = ({
  crmConnection,
}: DomusGlobalVariablesConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);

  const { data: merchant } = trpc.merchants.get.useQuery(
    { id: merchantId || "" },
    { enabled: !!merchantId }
  );

  const { data: campaignRules } = trpc.inboundCampaignRules.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const sourceEntries = useMemo<SourceEntry[]>(() => {
    const entries: SourceEntry[] = [];

    // Portal sources from merchant.portalSchemas
    const portalSchemas = (merchant as any)?.portalSchemas as
      | Array<{ source: string }>
      | undefined;
    if (portalSchemas) {
      for (const schema of portalSchemas) {
        entries.push({
          key: schema.source,
          label: schema.source,
          required: false,
          group: "portal",
        });
      }
    }

    // Campaign sources from active InboundCampaignRules
    const activeRules = (campaignRules ?? []).filter((r: any) => r.isActive);
    for (const rule of activeRules) {
      entries.push({
        key: `campaign:${rule.name}`,
        label: rule.name,
        required: false,
        group: "campaign",
      });
    }

    // Default (always last, always required)
    entries.push({ key: "default", label: "Default", required: true, group: "portal" });
    return entries;
  }, [merchant, campaignRules]);

  const [originSources, setOriginSources] = useState<Record<string, number>>({});
  const [availableSources, setAvailableSources] = useState<DomusSource[]>([]);
  const [availableTags, setAvailableTags] = useState<DomusTag[]>([]);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tagCode, setTagCode] = useState<number | undefined>(undefined);
  const [assignEmployeeMode, setAssignEmployeeMode] = useState<
    "disabled" | "overwrite" | "preserve"
  >("disabled");

  const utils = trpc.useUtils();
  const getSources = trpc.crmSelfConfig.getDomusSources.useMutation({
    onSuccess: (result) => {
      if (result.success && result.data) {
        setAvailableSources(result.data);
        setIsLoadingSources(false);
      } else if (!result.success) {
        toast.error("Error", {
          description: result.error || "No se pudieron obtener los orígenes.",
        });
        setIsLoadingSources(false);
      }
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al obtener los orígenes.",
      });
      setIsLoadingSources(false);
    },
  });

  const getTags = trpc.crmSelfConfig.getDomusTags.useMutation({
    onSuccess: (result) => {
      if (result.success && result.data) {
        setAvailableTags(result.data);
        setIsLoadingTags(false);
      } else if (!result.success) {
        toast.error("Error", {
          description: result.error || "No se pudieron obtener las etiquetas.",
        });
        setIsLoadingTags(false);
      }
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al obtener las etiquetas.",
      });
      setIsLoadingTags(false);
    },
  });

  const updateOriginSources = trpc.crmSelfConfig.updateDomusOriginSources.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.get.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuración guardada", {
        description: "Las variables globales se han guardado correctamente.",
      });
      setIsSaving(false);
      setIsEditing(false);
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al guardar las variables globales.",
      });
      setIsSaving(false);
    },
  });

  useEffect(() => {
    if (
      crmConnection?.provider === "domus" &&
      crmConnection?.domus?.apiKey &&
      availableSources.length === 0 &&
      !isLoadingSources
    ) {
      setIsLoadingSources(true);
      getSources.mutate({
        apiKey: crmConnection.domus.apiKey,
      });
    }
  }, [
    crmConnection?.provider,
    crmConnection?.domus?.apiKey,
    availableSources.length,
    isLoadingSources,
    getSources,
  ]);

  useEffect(() => {
    if (
      crmConnection?.provider === "domus" &&
      crmConnection?.domus?.apiKey &&
      crmConnection?.domus?.tagCode != null &&
      availableTags.length === 0 &&
      !isLoadingTags
    ) {
      setIsLoadingTags(true);
      getTags.mutate({ apiKey: crmConnection.domus.apiKey });
    }
  }, [
    crmConnection?.provider,
    crmConnection?.domus?.apiKey,
    crmConnection?.domus?.tagCode,
    availableTags.length,
    isLoadingTags,
    getTags,
  ]);

  useEffect(() => {
    if (crmConnection?.domus?.originSources) {
      const existingSources: Record<string, number> = {};
      const sources = crmConnection.domus.originSources as Array<{
        source: string;
        originId: number;
      }>;
      for (const entry of sources) {
        existingSources[entry.source] = entry.originId;
      }
      setOriginSources(existingSources);
    }
    if (crmConnection?.domus?.tagCode !== undefined) {
      setTagCode(crmConnection.domus.tagCode);
    }
    if (crmConnection?.domus?.assignEmployeeMode) {
      setAssignEmployeeMode(crmConnection.domus.assignEmployeeMode);
    } else if (crmConnection?.domus?.assignEmployee) {
      setAssignEmployeeMode("overwrite");
    }
  }, [
    crmConnection?.domus?.originSources,
    crmConnection?.domus?.tagCode,
    crmConnection?.domus?.assignEmployeeMode,
    crmConnection?.domus?.assignEmployee,
  ]);

  const handleSourceChange = (portalKey: string, sourceId: string) => {
    setOriginSources((prev) => {
      if (sourceId === "") {
        const next = { ...prev };
        delete next[portalKey];
        return next;
      }
      return { ...prev, [portalKey]: Number(sourceId) };
    });
  };

  const handleSave = () => {
    if (!merchantId || !crmConnection?.id) return;

    if (!originSources["default"]) {
      toast.error("Error", {
        description: 'Por favor selecciona un origen para "Default".',
      });
      return;
    }

    const originSourcesArray = Object.entries(originSources).map(
      ([source, originId]) => ({ source, originId })
    );

    setIsSaving(true);
    updateOriginSources.mutate({
      merchantId,
      id: crmConnection.id,
      originSources: originSourcesArray,
      tagCode,
      assignEmployeeMode,
    });
  };

  const hasConfiguration = () => {
    if (!crmConnection?.domus?.originSources) return false;
    const sources = crmConnection.domus.originSources as Array<{
      source: string;
      originId: number;
    }>;
    return sources.some((e: any) => e.source === "default" && e.originId > 0);
  };

  const getSourceLabel = (sourceId: number | undefined): string | null => {
    if (!sourceId) return null;
    const source = availableSources.find((s) => s.id === sourceId);
    return source?.name || `ID: ${sourceId}`;
  };

  if (crmConnection?.provider !== "domus") {
    return <></>;
  }

  const portalEntries = sourceEntries.filter((e) => e.group === "portal");
  const campaignEntries = sourceEntries.filter((e) => e.group === "campaign");

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-lg font-bold mb-2">Variables Globales</p>
            <p className="text-sm text-gray-600">
              Configura los orígenes para cada portal. El origen "Default" es obligatorio.
            </p>
          </div>
          <div className="flex flex-row gap-2">
            {hasConfiguration() ? (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                Configurado
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                Sin configurar
              </span>
            )}
            {!isEditing && (
              <Button
                variant="main"
                size="sm"
                onClick={() => {
                  setIsEditing(true);
                  const apiKey = crmConnection?.domus?.apiKey;
                  if (apiKey) {
                    if (availableSources.length === 0) {
                      setIsLoadingSources(true);
                      getSources.mutate({ apiKey });
                    }
                    if (availableTags.length === 0) {
                      setIsLoadingTags(true);
                      getTags.mutate({ apiKey });
                    }
                  }
                }}
              >
                Editar
              </Button>
            )}
          </div>
        </div>

        {isEditing ? (
          <>
            {isLoadingSources ? (
              <div className="flex flex-row gap-2">
                <Spinner size="sm" />
                <p className="text-sm text-gray-500">Cargando orígenes...</p>
              </div>
            ) : availableSources.length === 0 ? (
              <p className="text-sm text-gray-500">No se pudieron cargar los orígenes.</p>
            ) : (
              <div className="flex flex-col gap-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Portales
                </p>
                {portalEntries.map((entry) => {
                  const currentValue = originSources[entry.key];
                  return (
                    <div key={entry.key}>
                      <Label className="text-sm font-semibold text-gray-700">
                        {entry.label}
                        {entry.required && " *"}
                      </Label>
                      <div className="flex items-center gap-2">
                        <select
                          value={currentValue !== undefined ? String(currentValue) : ""}
                          onChange={(e) => handleSourceChange(entry.key, e.target.value)}
                          className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:border-gray-300 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                        >
                          <option value="">{`Selecciona un origen para ${entry.label}`}</option>
                          {availableSources.map((source) => (
                            <option key={source.id} value={source.id}>
                              {source.name}
                            </option>
                          ))}
                        </select>
                        {!entry.required && currentValue !== undefined && (
                          <button
                            type="button"
                            onClick={() => handleSourceChange(entry.key, "")}
                            className="min-h-11 min-w-11 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {campaignEntries.length > 0 && (
                  <>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-2">
                      Pauta
                    </p>
                    {campaignEntries.map((entry) => {
                      const currentValue = originSources[entry.key];
                      return (
                        <div key={entry.key}>
                          <Label className="text-sm font-semibold text-gray-700">
                            {entry.label}
                          </Label>
                          <div className="flex items-center gap-2">
                            <select
                              value={currentValue !== undefined ? String(currentValue) : ""}
                              onChange={(e) => handleSourceChange(entry.key, e.target.value)}
                              className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:border-gray-300 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                            >
                              <option value="">{`Selecciona un origen para ${entry.label}`}</option>
                              {availableSources.map((source) => (
                                <option key={source.id} value={source.id}>
                                  {source.name}
                                </option>
                              ))}
                            </select>
                            {currentValue !== undefined && (
                              <button
                                type="button"
                                onClick={() => handleSourceChange(entry.key, "")}
                                className="min-h-11 min-w-11 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="size-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                <div className="mt-4">
                  <Label className="text-sm font-semibold text-gray-700">Utilizar Etiqueta</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Opcional. Si seleccionas una etiqueta, se aplicará al crear el contacto en
                    Domus.
                  </p>
                  {availableTags.length === 0 && !isLoadingTags && crmConnection?.domus?.apiKey ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setIsLoadingTags(true);
                        getTags.mutate({ apiKey: crmConnection.domus.apiKey });
                      }}
                    >
                      Cargar etiquetas
                    </Button>
                  ) : isLoadingTags ? (
                    <div className="flex flex-row gap-2">
                      <Spinner size="sm" />
                      <p className="text-sm text-gray-500">Cargando etiquetas...</p>
                    </div>
                  ) : (
                    <select
                      value={tagCode ?? ""}
                      onChange={(e) =>
                        setTagCode(e.target.value ? Number(e.target.value) : undefined)
                      }
                      className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:border-gray-300 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                    >
                      <option value="">Selecciona una etiqueta (opcional)</option>
                      {availableTags.map((tag) => (
                        <option key={tag.code} value={tag.code}>
                          {tag.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="mt-4 space-y-2">
                  <Label className="text-sm font-semibold text-gray-700">
                    Asignación de asesor
                  </Label>
                  <RadioGroup
                    value={assignEmployeeMode}
                    onValueChange={(v) =>
                      setAssignEmployeeMode(v as "disabled" | "overwrite" | "preserve")
                    }
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="disabled" id="assign-disabled" />
                        <Label htmlFor="assign-disabled">Deshabilitado</Label>
                      </div>
                      <p className="text-xs text-gray-400 ml-6">
                        No se envía asignación de asesor a Domus. Solo se envía la sucursal.
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="overwrite" id="assign-overwrite" />
                        <Label htmlFor="assign-overwrite">
                          Sobreescribir asignación (Palomma es fuente de verdad)
                        </Label>
                      </div>
                      <p className="text-xs text-gray-400 ml-6">
                        Siempre envía el asesor de Palomma a Domus, incluso si el contacto ya tiene
                        uno asignado.
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="preserve" id="assign-preserve" />
                        <Label htmlFor="assign-preserve">
                          Preservar asignación en Domus (Domus es fuente de verdad)
                        </Label>
                      </div>
                      <p className="text-xs text-gray-400 ml-6">
                        Asigna el asesor solo al crear el contacto. Si ya existe asignación en
                        Domus, no la modifica.
                      </p>
                    </div>
                  </RadioGroup>
                </div>

                <div className="mt-4 flex flex-row gap-2">
                  <Button
                    variant="main"
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving || !originSources["default"]}
                  >
                    {isSaving && <Loader2 className="size-4 animate-spin" />}
                    Guardar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsEditing(false);
                      // Reset to saved values
                      if (crmConnection?.domus?.originSources) {
                        const existingSources: Record<string, number> = {};
                        const sources = crmConnection.domus.originSources as Array<{
                          source: string;
                          originId: number;
                        }>;
                        for (const entry of sources) {
                          existingSources[entry.source] = entry.originId;
                        }
                        setOriginSources(existingSources);
                      } else {
                        setOriginSources({});
                      }
                      if (crmConnection?.domus?.tagCode !== undefined) {
                        setTagCode(crmConnection.domus.tagCode);
                      } else {
                        setTagCode(undefined);
                      }
                      if (crmConnection?.domus?.assignEmployeeMode) {
                        setAssignEmployeeMode(crmConnection.domus.assignEmployeeMode);
                      } else if (crmConnection?.domus?.assignEmployee) {
                        setAssignEmployeeMode("overwrite");
                      } else {
                        setAssignEmployeeMode("disabled");
                      }
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col gap-2">
            {hasConfiguration() ? (
              <>
                {(
                  crmConnection.domus.originSources as Array<{
                    source: string;
                    originId: number;
                  }>
                )
                  .filter((entry) => {
                    if (!entry.source.startsWith("campaign:")) return true;
                    const campaignName = entry.source.replace("campaign:", "");
                    return (campaignRules ?? []).some(
                      (r: any) => r.isActive && r.name === campaignName
                    );
                  })
                  .map((entry) => {
                    const label = entry.source.startsWith("campaign:")
                      ? `Pauta - ${entry.source.replace("campaign:", "")}`
                      : entry.source === "default"
                        ? "Default"
                        : entry.source;
                    const sourceLabel = getSourceLabel(entry.originId);
                    return (
                      <div key={entry.source} className="flex items-center justify-between text-xs">
                        <p className="font-medium text-gray-600">{label}:</p>
                        <p className="text-gray-700">{sourceLabel}</p>
                      </div>
                    );
                  })}
                <div className="mt-2 flex items-center justify-between border-t border-gray-200 pt-2 text-xs">
                  <p className="font-medium text-gray-600">Etiqueta:</p>
                  <p className="text-gray-700">
                    {crmConnection?.domus?.tagCode != null
                      ? (availableTags.find((t) => t.code === crmConnection.domus.tagCode)?.name ??
                        `Código ${crmConnection.domus.tagCode}`)
                      : "Ninguna"}
                  </p>
                </div>
                <div className="flex items-center justify-between border-t border-gray-200 pt-2 text-xs">
                  <p className="font-medium text-gray-600">Asignación de asesor:</p>
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
                    {(() => {
                      const mode =
                        crmConnection?.domus?.assignEmployeeMode ??
                        (crmConnection?.domus?.assignEmployee ? "overwrite" : "disabled");
                      const labels: Record<string, string> = {
                        disabled: "Deshabilitado",
                        overwrite: "Sobreescribir",
                        preserve: "Preservar en Domus",
                      };
                      return labels[mode] ?? "Deshabilitado";
                    })()}
                  </span>
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-500">
                Haz clic en Editar para configurar las variables globales.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DomusGlobalVariablesConfiguration;
